import { defineStore } from 'pinia';
import { type StorageLike, useStorage } from '@vueuse/core';
import AuthService from '@/services/authService';
import { usePreferencesStore } from '@/stores/deepadmin/preferences';
import { LocalPreferenceKeys } from '@/types/enums/LocalPreferenceKeys';
import type { Preferences } from '@/types/deepadmin/Preferences';

const NAMESPACE = 'deepsign';

const MIGRATED_KEY = 'prefsMigrated';

// Used when nobody is signed in. It never reaches localStorage — an anonymous
// load gets the in-memory storage below — it only keeps the key shape uniform.
const ANONYMOUS_SCOPE = 'anonymous';

// Everything this store owns, so `pruneScopes` recognises its own entries and
// leaves every other `deepsign.*` key alone (e.g. publicWizardCompleted).
const SCOPED_KEYS: string[] = [
  ...Object.values(LocalPreferenceKeys),
  MIGRATED_KEY
];

// The user scopes present on this device, most recently signed in first.
const SCOPE_INDEX_KEY = `${NAMESPACE}.preferenceScopes`;
const MAX_SCOPES = 5;

// Keys only materialize once something is actually written, so a user who never
// touches a setting doesn't litter storage with a full set of defaults.
const STORAGE_OPTIONS = { writeDefaults: false };

const LEGACY_KEYS: string[] = Object.values(LocalPreferenceKeys);

/**
 * `deepsign.<userId>.<key>` — the shape `useUtils` already uses for its
 * per-user session keys. These preferences are personal, and the same device is
 * routinely shared by several accounts (shared workstation, private + work
 * login), so they must not be read or overwritten across users:
 * `lastSelectedOrg` points at an organization the next user may not belong to
 * and `documentsWizards` holds document ids that mean nothing to anyone else.
 */
export function userScopedKey(key: string) {
  const userId = AuthService.user?.sub;
  return `${NAMESPACE}.${userId || ANONYMOUS_SCOPE}.${key}`;
}

/**
 * Anonymous loads (Keycloak runs check-sso, and the public document routes
 * mount without a user) have no scope to persist into, so their values live for
 * the lifetime of the page instead of leaving behind keys no account will ever
 * claim.
 */
function createMemoryStorage(): StorageLike {
  const values = new Map<string, string>();
  return {
    getItem: (key) => values.get(key) ?? null,
    setItem: (key, value) => {
      values.set(key, value);
    },
    removeItem: (key) => {
      values.delete(key);
    }
  };
}

// The scope a stored key belongs to, or undefined if it isn't one of ours.
function scopeOf(storageKey: string): string | undefined {
  const prefix = `${NAMESPACE}.`;
  if (!storageKey.startsWith(prefix)) return undefined;
  const owned = SCOPED_KEYS.find((key) => storageKey.endsWith(`.${key}`));
  if (!owned) return undefined;
  return storageKey.slice(prefix.length, -(owned.length + 1)) || undefined;
}

/**
 * Keep the settings of at most MAX_SCOPES accounts on this device, most
 * recently signed in first. localStorage never expires, so without this every
 * account that ever signed in here would keep its keys forever.
 */
function pruneScopes(userId: string) {
  try {
    const raw = localStorage.getItem(SCOPE_INDEX_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    const known = Array.isArray(parsed)
      ? parsed.filter((scope): scope is string => typeof scope === 'string')
      : [];
    const kept = [userId, ...known.filter((scope) => scope !== userId)].slice(
      0,
      MAX_SCOPES
    );

    // Drops the evicted tail plus anything orphaned by an index that was
    // cleared or never written.
    for (const key of Object.keys(localStorage)) {
      const scope = scopeOf(key);
      if (scope && !kept.includes(scope)) localStorage.removeItem(key);
    }

    localStorage.setItem(SCOPE_INDEX_KEY, JSON.stringify(kept));
  } catch (error) {
    // Unreadable index or storage unavailable (private mode / quota): the
    // preferences themselves still work, only the cleanup is skipped.
    console.error('Failed to prune local preference scopes', error);
  }
}

// Leftover profile shape for documentsWizards (`{ documentId, wizardCompleted }`).
// Only the migration below still has to understand it — once the profile keys are
// purged, the local value is always a plain string[] of pending document ids.
interface LegacyDocumentWizard {
  documentId: string;
  wizardCompleted: boolean;
}

function isLegacyWizardEntry(item: unknown): item is LegacyDocumentWizard {
  return (
    !!item &&
    typeof item === 'object' &&
    'documentId' in item &&
    'wizardCompleted' in item
  );
}

function pendingIdsFrom(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((item) => {
    if (typeof item === 'string' && item) return [item];
    if (
      isLegacyWizardEntry(item) &&
      item.wizardCompleted === false &&
      item.documentId
    ) {
      return [item.documentId];
    }
    return [];
  });
}

/**
 * Preferences that used to be stored on the DeepAdmin profile endpoint and are
 * now device-local. The profile payload is capped (see DEEPBOX-11085), so only
 * `recentlyUsedEmails` and `defaultAuthorityService` still live there — see
 * usePreferencesStore.
 *
 * Values are scoped to the signed-in user (see `userScopedKey`), so several
 * accounts can share a device without seeing each other's settings. They still
 * do not follow a user to another device: that is accepted for view mode,
 * expert settings and the last selected org; for `documentsWizards` it means a
 * draft set up on one device replays its setup wizard on another.
 *
 * The scope is resolved once, when the store is first used. That is safe
 * because the app only mounts after Keycloak has settled (main.ts), and
 * switching account always goes through a Keycloak redirect, which reloads the
 * app — the signed-in user can never change underneath a live store.
 */
export const useLocalPreferencesStore = defineStore('localPreferences', () => {
  const preferencesStore = usePreferencesStore();

  const userId = AuthService.user?.sub;
  if (userId) pruneScopes(userId);
  const storage: StorageLike = userId ? localStorage : createMemoryStorage();

  const viewOption = useStorage<'grid' | 'list'>(
    userScopedKey(LocalPreferenceKeys.VIEW_OPTION),
    'grid',
    storage,
    STORAGE_OPTIONS
  );
  const expertSettings = useStorage(
    userScopedKey(LocalPreferenceKeys.EXPERT_SETTINGS),
    false,
    storage,
    STORAGE_OPTIONS
  );
  const quorumControl = useStorage(
    userScopedKey(LocalPreferenceKeys.QUORUM_CONTROL),
    false,
    storage,
    STORAGE_OPTIONS
  );
  // `null` rather than `undefined`: useStorage picks its serializer from the
  // type of the default value.
  const lastSelectedOrg = useStorage<string | null>(
    userScopedKey(LocalPreferenceKeys.LAST_SELECTED_ORG),
    null,
    storage,
    STORAGE_OPTIONS
  );
  const documentsWizards = useStorage<string[]>(
    userScopedKey(LocalPreferenceKeys.DOCUMENTS_WIZARDS),
    [],
    storage,
    STORAGE_OPTIONS
  );
  const createdDateOrder = useStorage<'creationTime desc' | 'creationTime asc'>(
    userScopedKey(LocalPreferenceKeys.CREATED_DATE_ORDER),
    'creationTime desc',
    storage,
    STORAGE_OPTIONS
  );
  const migrated = useStorage(
    userScopedKey(MIGRATED_KEY),
    false,
    storage,
    STORAGE_OPTIONS
  );

  /**
   * One-off move of this account's legacy profile values onto this device,
   * followed by deleting them server side so they stop eating the capped
   * profile payload.
   *
   * `migrated` lives in the user's own scope and exists for exactly one
   * situation: the seed succeeded but the purge did not, so the next boot still
   * sees the profile keys and must not seed a second time over a value the user
   * has changed in between. It is therefore written only when something was
   * actually seeded, and dropped again once the purge is confirmed.
   */
  async function migrateFromProfile(remote: Preferences | undefined) {
    const stale = LEGACY_KEYS.filter(
      (key) => remote?.[key] !== undefined && remote?.[key] !== null
    );
    // Nothing left on the profile: already migrated, or this account never had
    // the keys. Either way there is nothing to seed and nothing to purge.
    if (!stale.length) return;

    if (!migrated.value) {
      const remoteViewOption = remote?.[LocalPreferenceKeys.VIEW_OPTION];
      if (remoteViewOption === 'grid' || remoteViewOption === 'list') {
        viewOption.value = remoteViewOption;
      }
      const remoteExpertSettings =
        remote?.[LocalPreferenceKeys.EXPERT_SETTINGS];
      if (typeof remoteExpertSettings === 'boolean') {
        expertSettings.value = remoteExpertSettings;
      }
      const remoteLastSelectedOrg =
        remote?.[LocalPreferenceKeys.LAST_SELECTED_ORG];
      if (typeof remoteLastSelectedOrg === 'string' && remoteLastSelectedOrg) {
        lastSelectedOrg.value = remoteLastSelectedOrg;
      }
      const remoteWizards = remote?.[LocalPreferenceKeys.DOCUMENTS_WIZARDS];
      if (Array.isArray(remoteWizards)) {
        documentsWizards.value = pendingIdsFrom(remoteWizards);
      }
      const remoteCreatedDateOrder =
        remote?.[LocalPreferenceKeys.CREATED_DATE_ORDER];
      if (
        remoteCreatedDateOrder === 'creationTime desc' ||
        remoteCreatedDateOrder === 'creationTime asc'
      ) {
        createdDateOrder.value = remoteCreatedDateOrder;
      }
      migrated.value = true;
    }

    try {
      await preferencesStore.removePreferences(LEGACY_KEYS);
      // Purge confirmed, so the guard has no job left.
      migrated.value = null;
    } catch (error) {
      console.error('Failed to purge legacy profile preferences', error);
    }
  }

  return {
    viewOption,
    expertSettings,
    quorumControl,
    lastSelectedOrg,
    documentsWizards,
    createdDateOrder,
    migrated,
    migrateFromProfile
  };
});
